package er.prateek.crossly.progression

import androidx.compose.runtime.mutableStateMapOf
import er.prateek.crossly.data.model.DifficultyTier
import er.prateek.crossly.data.model.GameMode
import er.prateek.crossly.firebase.CompiledDefaultsRemoteConfigClient
import er.prateek.crossly.firebase.RemoteConfigClient
import er.prateek.crossly.firebase.RemoteConfigKeys
import er.prateek.crossly.persistence.ProgressStore

/**
 * Compose-observable progress for a single leveled game mode (math crossword or riddles).
 *
 * A twin of [ProgressionManager], but mode-scoped and content-free: where [ProgressionManager]
 * takes `List<Puzzle>` to know how many levels a tier has, this takes a [levelsPerTier] count,
 * because the new modes generate their content lazily and never materialise a puzzle just to
 * draw the level map. Stars/times live in [er.prateek.crossly.data.model.UserProgress]'s
 * per-mode maps, keyed by [GameMode.name], so each mode and the original crossword progress
 * stay fully independent inside the one shared `user_progress` record.
 */
class ModeProgressionManager(
    val mode: GameMode,
    private val store: ProgressStore,
    private val levelsPerTier: (DifficultyTier) -> Int,
    private val remoteConfig: RemoteConfigClient = CompiledDefaultsRemoteConfigClient,
) {
    private val bestStars = mutableStateMapOf<String, Int>().apply {
        putAll(store.load().modeStarsByLevelId[mode.name].orEmpty())
    }
    private val bestTimes = mutableStateMapOf<String, Long>().apply {
        putAll(store.load().modeBestTimeMsByLevelId[mode.name].orEmpty())
    }

    /** Stable id for a level, 1-based within its tier: e.g. `math-easy-001`. */
    fun levelId(tier: DifficultyTier, index: Int): String =
        "${mode.slug}-${tier.name.lowercase()}-${index.toString().padStart(3, '0')}"

    fun starsFor(levelId: String): Int = bestStars[levelId] ?: 0

    fun bestTimeMs(levelId: String): Long? = bestTimes[levelId]

    fun hasAnyProgress(): Boolean = bestStars.isNotEmpty()

    fun totalStars(): Int = bestStars.values.sum()

    /** Levels cleared at least once. */
    fun completedCount(): Int = bestStars.count { it.value >= 1 }

    /** Levels cleared for the full three stars. */
    fun perfectCount(): Int = bestStars.count { it.value >= 3 }

    /**
     * Records a cleared level, keeping the best stars and fastest time. Read-modify-writes the
     * shared [UserProgress] blob, touching only this mode's slice of the per-mode maps -- never
     * the crossword stars or the daily streak that share the same settings key.
     */
    fun recordCompletion(levelId: String, hintsUsed: Int, mistakes: Int, elapsedMs: Long = 0): Int {
        val earned = ScoringPolicy.starsFor(hintsUsed, mistakes)
        var changed = false
        if (earned > (bestStars[levelId] ?: 0)) {
            bestStars[levelId] = earned
            changed = true
        }
        if (elapsedMs > 0) {
            val best = bestTimes[levelId]
            if (best == null || elapsedMs < best) {
                bestTimes[levelId] = elapsedMs
                changed = true
            }
        }
        if (changed) {
            val current = store.load()
            store.save(
                current.copy(
                    modeStarsByLevelId = current.modeStarsByLevelId + (mode.name to bestStars.toMap()),
                    modeBestTimeMsByLevelId = current.modeBestTimeMsByLevelId + (mode.name to bestTimes.toMap()),
                ),
            )
        }
        return earned
    }

    /** Clears only this mode's progress, leaving crossword stars and other modes untouched. */
    fun resetProgress() {
        bestStars.clear()
        bestTimes.clear()
        val current = store.load()
        store.save(
            current.copy(
                modeStarsByLevelId = current.modeStarsByLevelId - mode.name,
                modeBestTimeMsByLevelId = current.modeBestTimeMsByLevelId - mode.name,
            ),
        )
    }

    /** EASY is always open; a later tier unlocks once the previous tier is [tierUnlockThresholdPct]-starred. */
    fun isTierUnlocked(tier: DifficultyTier): Boolean {
        if (tier == DifficultyTier.EASY) return true
        val previousTier = DifficultyTier.entries[tier.ordinal - 1]
        val count = levelsPerTier(previousTier)
        if (count <= 0) return false
        val starred = (1..count).count { starsFor(levelId(previousTier, it)) >= 1 }
        return starred.toDouble() / count >= tierUnlockThresholdPct()
    }

    /** Level [index] (0-based) is open when its tier is unlocked and the previous level has >=1 star. */
    fun isLevelUnlocked(tier: DifficultyTier, index: Int): Boolean {
        if (!isTierUnlocked(tier)) return false
        if (index <= 0) return true
        // Previous level's 1-based number equals this level's 0-based index.
        return starsFor(levelId(tier, index)) >= 1
    }

    /** Mirrors [ProgressionManager.tierUnlockThresholdPct]: Remote-Config tunable, clamped, with a safe default. */
    private fun tierUnlockThresholdPct(): Double {
        val pct = remoteConfig.getLong(RemoteConfigKeys.TIER_UNLOCK_THRESHOLD_PCT)
        return if (pct in 1..100) pct / 100.0 else ProgressionManager.TIER_UNLOCK_THRESHOLD_PCT
    }
}
